/**
 * Load a team's branding and display its configured name/logo, falling back to the supplied name.
 *
 * @module apps/web/src/components/teams/TeamBrandIdentity
 */

import { useEffect } from 'react';
import type { TeamBrandingProfile } from '@fluxability/types';
import { BrandAssetImage } from './TeamBranding';
import { teamPath, useTeamResource } from './shared';
/**
 * Load a team's branding and display its configured name/logo, falling back to the supplied name.
 */
export function TeamBrandIdentity({ teamId, name }: { /** Selected team ID used to scope API requests; the server validates membership and permission. */
teamId: string; /** Team display name used for labels and the fallback identity initial. */
name: string }) {
  const resource = useTeamResource<{ /** Stored or default branding. */ branding: TeamBrandingProfile }>(
    teamPath(teamId, 'branding')
  );
  const branding = resource.data?.branding;
  const { reload } = resource;
  useEffect(() => {
    const handler = () => reload();
    window.addEventListener('fluxability-branding-updated', handler);
    return () => window.removeEventListener('fluxability-branding-updated', handler);
  }, [reload]);
  const primary = branding?.primary_color;
  const color = primary && /^#[\da-f]{6}$/i.test(primary) ? primary : undefined;
  return (
    <span className="flex min-w-0 items-center gap-2">
      {branding?.logo_asset_id && (
        <BrandAssetImage
          key={branding.logo_asset_id}
          teamId={teamId}
          assetId={branding.logo_asset_id}
          name={name}
        />
      )}
      <span
        className="min-w-0 truncate"
        style={{ borderBottom: color ? `2px solid ${color}` : undefined }}
      >
        {branding?.display_name || name}
      </span>
    </span>
  );
}
